/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { FC, useCallback } from 'react'
import { TouchableOpacity, View } from 'react-native'

import { Network } from '@ambire-common/interfaces/network'
import CopyIcon from '@common/assets/svg/CopyIcon'
import LinkIcon from '@common/assets/svg/LinkIcon'
import OpenIcon from '@common/assets/svg/OpenIcon'
import Button from '@common/components/Button'
import Text from '@common/components/Text'
import { useTranslation } from '@common/config/localization'
import useTheme from '@common/hooks/useTheme'
import useToast from '@common/hooks/useToast'
import useWindowSize from '@common/hooks/useWindowSize'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import { setStringAsync } from '@common/utils/clipboard'
import { openInTab } from '@common/utils/links'

import getStyles from './styles'

type Props = {
  network: Network
  txnId?: string
  /** RAILGUN ShieldBlocked — surfaces the recover / PPOI CTAs below the buttons. */
  isBlocked?: boolean
  ppoiUrl?: string
  onUnshieldToOrigin?: () => void
}

/**
 * Footer of the PRIVATE activity detail sheet. Mirrors the public SummaryFooter
 * (Open explorer / Copy link) so both detail views close with the same button
 * bar — see Trello #467. "Repeat"/"Speed up" are intentionally absent: a RAILGUN
 * history item has no replayable AccountOp calls.
 */
const PrivateSummaryFooter: FC<Props> = ({
  network,
  txnId,
  isBlocked = false,
  ppoiUrl,
  onUnshieldToOrigin
}) => {
  const { styles, theme } = useTheme(getStyles)
  const { addToast } = useToast()
  const { t } = useTranslation()
  const { minWidthSize } = useWindowSize()
  const isNarrow = minWidthSize('s')

  const explorerLink =
    txnId && network.explorerUrl ? `${network.explorerUrl.replace(/\/$/, '')}/tx/${txnId}` : ''

  const handleOpenExplorer = useCallback(async () => {
    if (!explorerLink) {
      addToast(
        t("Can't open the transaction details because the block explorer link is missing."),
        {
          type: 'error'
        }
      )
      return
    }

    try {
      await openInTab({ url: explorerLink })
    } catch (e: any) {
      addToast(e?.message || t('Error opening explorer'), { type: 'error' })
    }
  }, [explorerLink, addToast, t])

  const handleCopyLink = useCallback(() => {
    if (!explorerLink) {
      addToast(t("Can't copy the transaction link because the block explorer link is missing."), {
        type: 'error'
      })
      return
    }

    setStringAsync(explorerLink)
    addToast(t('Copied to clipboard!') as string, { timeout: 2500 })
  }, [explorerLink, addToast, t])

  return (
    <View style={[styles.footer, isNarrow && styles.footerNarrow]}>
      <View style={[styles.footerButtonsRow, isNarrow && styles.footerButtonsRowNarrow]}>
        <Button
          text={t('Open explorer')}
          type="outline"
          onPress={handleOpenExplorer}
          size="small"
          disabled={!explorerLink}
          hasBottomSpacing={false}
          style={[styles.footerButton, isNarrow && styles.footerButtonNarrow]}
          childrenPosition="left"
          testID="view-transaction-link"
        >
          <OpenIcon style={spacings.mrTy} width={16} height={16} />
        </Button>

        <View style={styles.footerRightButtonsGroup}>
          <Button
            // On narrow phone-browser viewports collapse to an icon-only button,
            // same as the public footer.
            text={isNarrow ? undefined : t('Copy link')}
            accessibilityLabel={t('Copy link')}
            onPress={handleCopyLink}
            type="primary"
            size="small"
            disabled={!explorerLink}
            hasBottomSpacing={false}
            style={[styles.footerButton, isNarrow && styles.footerCopyIconButtonNarrow]}
            childrenPosition="left"
            testID="copy-transaction-hash-link"
          >
            <CopyIcon style={isNarrow ? undefined : spacings.mrTy} width={16} height={16} />
          </Button>
        </View>
      </View>
      {isBlocked && (onUnshieldToOrigin || ppoiUrl) ? (
        <View
          style={[
            flexbox.directionRow,
            flexbox.wrap,
            flexbox.alignCenter,
            spacings.ptMd,
            { columnGap: 16, rowGap: 8 }
          ]}
        >
          {onUnshieldToOrigin ? (
            <TouchableOpacity
              style={[flexbox.directionRow, flexbox.alignCenter]}
              onPress={onUnshieldToOrigin}
            >
              <Text
                fontSize={14}
                weight="semiBold"
                style={[spacings.mrMi, { color: theme.warningText as string }]}
                underline
              >
                {t('Unshield to origin')}
              </Text>
            </TouchableOpacity>
          ) : null}
          {ppoiUrl ? (
            <TouchableOpacity
              style={[flexbox.directionRow, flexbox.alignCenter]}
              onPress={() => openInTab({ url: ppoiUrl })}
            >
              <Text
                fontSize={14}
                appearance="secondaryText"
                weight="medium"
                style={spacings.mrMi}
                underline
              >
                {t('View on PPOI.info')}
              </Text>
              <LinkIcon width={14} height={14} />
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}
    </View>
  )
}

export default PrivateSummaryFooter
